export interface CookieYesCategories {
	statistics: boolean;
	marketing: boolean;
	preferences: boolean;
}

export const COOKIEYES_COOKIE = "cookieyes-consent";

/**
 * CookieYes stores its decision as `key:value` pairs, e.g.
 * "consentid:abc,consent:yes,necessary:yes,functional:yes,analytics:yes,performance:yes,advertisement:yes".
 *
 * Returns `null` when the string carries none of the mapped categories, so an
 * absent or unrelated cookie reads as "unknown" rather than "everything denied"
 * — the latter would record a decision the user never made.
 */
export const parseCookieYesConsent = (
	raw: string | undefined | null
): CookieYesCategories | null => {
	if (!raw) return null;

	let value = raw;
	try {
		value = decodeURIComponent(raw);
	} catch {
		// A stray `%` makes decoding throw; the raw string is still parseable.
	}

	const pairs: Record<string, string> = {};
	for (const pair of value.split(",")) {
		const [key, ...rest] = pair.trim().split(":");
		if (key) pairs[key] = rest.join(":");
	}

	const known = ["analytics", "advertisement", "functional"];
	if (!known.some((key) => key in pairs)) return null;

	// `action` is CookieYes' "the visitor has actually chosen" flag. Treat a
	// pre-decision cookie as unknown so we never record a decision nobody made.
	if ("action" in pairs && pairs.action !== "yes") return null;

	// Mapping matches the CookieYes GTM template, so this store and Consent Mode
	// can never disagree. `performance` is unmapped there too.
	return {
		statistics: pairs.analytics === "yes",
		marketing: pairs.advertisement === "yes",
		preferences: pairs.functional === "yes"
	};
};

/** Pulls the raw CookieYes cookie out of a `document.cookie` string. */
export const readCookieYesCookie = (cookieString: string): string | undefined =>
	cookieString
		.split(";")
		.map((c) => c.trim())
		.find((c) => c.startsWith(`${COOKIEYES_COOKIE}=`))
		?.slice(COOKIEYES_COOKIE.length + 1);
